import { FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_COMMUNITY_POSTS,
  CREATE_COMMUNITY_POST,
} from "../../api/community";
import { useAuth } from "../../core/auth";
import { notify, notifyError } from "../../utils/toast";
import { getErrorMessage } from "../../utils/error";
import { maskName } from "../../utils/mask-name";
import { useUser } from "../../core/user-context";

type CommunityPost = {
  id: string;
  content: string;
  authorName: string;
  authorId?: number | null;
  status: string;
  createdAt: string;
  replyCount: number;
  replies: Array<{
    id: string;
    content: string;
    authorName: string;
    isAdmin: boolean;
    createdAt: string;
  }>;
};

const Community: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { user } = useUser();
  const [content, setContent] = useState("");

  const { data, loading, refetch } = useQuery(GET_COMMUNITY_POSTS, {
    variables: { offset: 0, limit: 100 },
    skip: false,
  });

  const [createPost, { loading: creating }] = useMutation(
    CREATE_COMMUNITY_POST,
    {
      onCompleted: () => {
        setContent("");
        refetch();
        notify("피드백이 등록되었습니다!");
      },
      onError: (error) => {
        console.error("게시글 작성 실패:", error);
        const errorMessage = getErrorMessage(error);
        notifyError(`게시글 작성 실패: ${errorMessage}`);
      },
    }
  );

  const posts: CommunityPost[] = useMemo(
    () => data?.communityPosts || [],
    [data?.communityPosts]
  );
  const empty = useMemo(() => !loading && posts.length === 0, [loading, posts]);
  const canSubmit = useMemo(
    () => content.trim().length > 0 && !creating,
    [content, creating]
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    if (!canSubmit) return;
    createPost({
      variables: {
        input: {
          content: content.trim(),
        },
      },
    });
  };

  return (
    <div className="px-4 py-5">
      <button className="text-sm mb-3" onClick={() => navigate(-1)}>
        ← 뒤로
      </button>
      <h1 className="text-lg font-semibold mb-4">👨‍💻개발자와 소통하기</h1>

      <div className="mb-4 p-3 bg-[#F4F5F7] rounded-lg text-sm text-gray-600 space-y-1">
        <p>추가되었으면 하는 기능, 버그 신고 등 하고싶은 말을 적어주세요!</p>
        <p>작성 즉시 개발자에게 알림이 갑니다.</p>
        <p className="font-semibold text-[#1562fc]">
          ℹ️ 모든 게시물은 공개됩니다.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mb-6 border-b pb-6">
        <div className="border rounded-md p-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onClick={() => {
              if (!isLoggedIn) {
                navigate("/login");
              }
            }}
            onFocus={() => {
              if (!isLoggedIn) {
                navigate("/login");
              }
            }}
            placeholder={
              isLoggedIn ? "피드백을 입력하세요" : "로그인이 필요합니다"
            }
            rows={4}
            className="w-full border-0 px-0 py-0 text-sm resize-none focus:outline-none"
            disabled={!isLoggedIn || creating}
          />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-3 py-2 rounded-md text-sm ${
                canSubmit
                  ? "bg-[#1562fc] text-white hover:bg-[#124ab7]"
                  : "bg-[#F4F5F7] text-[#BBBDC0]"
              }`}
            >
              {creating ? "등록 중..." : "등록"}
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="text-sm text-gray-500">로딩 중...</div>
      ) : empty ? (
        <div className="text-sm text-gray-500">아직 게시글이 없습니다.</div>
      ) : (
        <ul className="space-y-3">
          {posts.map((post) => {
            const hasReply = post.replyCount > 0;
            const hasAdminReply = post.replies?.some((r) => r.isAdmin) || false;
            const status = post.status?.toUpperCase();
            const isAnswered = status === "ANSWERED";
            const isMyPost = user?.id && post.authorId === user.id;
            return (
              <li key={post.id}>
                <button
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${
                    hasReply
                      ? isAnswered || hasAdminReply
                        ? "bg-[#F4F5F7] border-[#1562fc]"
                        : "bg-[#F4F5F7] border-[#F4F5F7]"
                      : "bg-white border-[#F4F5F7]"
                  }`}
                  onClick={() => navigate(`/community/detail/${post.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      {isMyPost && (
                        <span className="text-[#1562fc] font-medium">
                          내 글
                        </span>
                      )}
                      {maskName(post.authorName)} ·{" "}
                      {new Date(post.createdAt).toLocaleString()}
                    </div>
                    {hasReply && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          isAnswered || hasAdminReply
                            ? "bg-[#1562fc] text-white"
                            : "bg-[#F4F5F7] text-[#1562fc]"
                        }`}
                      >
                        {isAnswered || hasAdminReply
                          ? "✓ 운영자 답변"
                          : `${post.replyCount}개 답변`}
                      </span>
                    )}
                  </div>
                  {post.content && (
                    <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                      {post.content}
                    </p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Community;
